<template>
    <div class="feature" :class="{ disabled, [type]: true }" @click="(e) => toggleSwitch(e)">
        <div class="left">
            <div class="icon">
                <component :is="icon" />
            </div>
            <div class="texts">
                <div class="feature-title title-color">
                    {{ title }}
                </div>
                <div class="feature-description">
                    {{ description }}
                </div>
                <slot />
            </div>
        </div>
        <div class="right" @click="(e) => e.stopPropagation()">
            <a-switch
                v-if="type === 'switch'"
                class="md-warning ml-3"
                name="writing"
                :required="required"
                v-model="clone"
                checked-children="✔"
                un-checked-children="✖"
                :disabled="disabled"
                :checked="clone"
            />

            <a-select
                v-if="type === 'select'"
                ref="select"
                style="width: 202px"
                v-model="clone"
                v-validate="required ? 'required' : ''"
                autocomplete="off"
            >
                <a-select-option :value="undefined" v-if="showNullSelectOption"
                    >{{ defaultSelectOptionPlaceholder }}
                </a-select-option>
                <a-select-option v-for="(option, index) of options" :key="index" :value="option.id">
                    {{ option.label }}
                </a-select-option>
            </a-select>

            <div class="number-wrapper" v-if="type === 'number'">
                <a-button type="dashed" shape="circle" icon="minus" :disabled="clone <= min" @click="decreaseValue" />
                <a-input
                    class="text-align-center"
                    v-model="clone"
                    v-validate="getNumericRequireText()"
                    :max="max"
                    :min="min"
                    :step="step"
                    autocomplete="off"
                    :placeholder="numberPlaceHolder"
                    trim
                    type="number"
                />
                <a-button type="dashed" shape="circle" icon="plus" :disabled="clone >= max" @click="increaseValue" />
            </div>
        </div>
    </div>
</template>

<script>
import * as _ from "lodash";
import log2 from "../../../../utils/log2";

const log = log2("FeatureItem");
export default {
    name: "FeatureItem",
    props: {
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean | Number | String,
            required: true,
        },
        icon: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: true,
        },
        checked: {
            type: Boolean,
            default: true,
        },
        type: {
            type: String,
            default: "switch",
            enum: ["switch", "select", "number"],
        },
        options: {
            type: Array,
            default: () => [],
        },
        defaultSelectOptionPlaceholder: {
            type: String,
            default: "Select option",
        },
        showNullSelectOption: {
            type: Boolean,
            default: true,
        },
        numberPlaceHolder: {
            type: String,
            default: "Must be >= 1min and <= 60min",
        },
        min: {
            type: Number,
            default: 1,
        },
        max: {
            type: Number,
            default: 60,
        },
        step: {
            type: Number,
            default: 1,
        },
        numberFormat: {
            type: String,
            default: "integer",
            enum: ["integer", "float"],
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
        };
    },
    methods: {
        getNumericRequireText() {
            return `required|numeric|min_value:${this.min}|max_value:${this.max}`;
        },
        toggleSwitch(e) {
            log("toggleSwitch", this.type, e.currentTarget);
            if (this.type === "switch" && !this.disabled) {
                e.preventDefault();
                e.stopPropagation();
                this.clone = !this.clone;
            }
        },
        increaseValue() {
            if (this.numberFormat === "float") {
                this.clone = parseFloat(this.clone) + parseFloat((this.step || 1).toString());
            } else {
                this.clone = parseInt(this.clone) + parseFloat((this.step || 1).toString());
            }
        },
        decreaseValue() {
            if (this.numberFormat === "float") {
                this.clone = parseFloat(this.clone) - parseFloat((this.step || 1).toString());
            } else {
                this.clone = parseInt(this.clone) - parseFloat((this.step || 1).toString());
            }
        },
    },
    watch: {
        clone(val) {
            this.$emit("input", val);
        },
    },
};
</script>

<style scoped lang="scss">
.feature {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 10px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    background: var(--color-bg-neutral-default);
    width: 900px;

    &.switch {
        cursor: pointer;
    }

    &:hover {
        background: var(--color-bg-neutral-hover);
        border: 1px solid var(--color-border-hover);
    }

    .left {
        display: flex;
        align-items: center;
        gap: 20px;

        .icon {
            svg {
                width: 40px;
                height: 40px;
                fill: var(--color-bg-primary-default);
            }
        }

        .feature-title {
            font-size: 16px;
            font-weight: 500;
            color: var(--color-text-strong-default);
        }

        .feature-description {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-low-default);
        }
    }
}

.right {
    display: flex !important;
    align-items: center !important;

    .ant-switch-checked {
        background-color: var(--color-bg-primary-default);
    }

    .number-wrapper {
        display: flex;
        gap: 5px;
        align-items: center;
    }
}

.dark-mode .feature {
    background: #1e1e2d;

    .left {
        display: flex;
        align-items: center;
        gap: 20px;

        .icon {
            svg {
                width: 40px;
                height: 40px;
                fill: var(--color-bg-primary-default);
            }
        }

        .feature-title {
            font-size: 16px;
            font-weight: 500;
            color: #fff;
        }

        .feature-description {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-low-default);
        }
    }
}
</style>
